import React, { SVGProps } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, LabelList, LabelProps } from 'recharts';
import { SizeMeProps, withSize } from 'react-sizeme';

import { RadialChartItem } from 'features/dashboard/ts/RadialChartItem';
import { ORDER_SUMMARY_COLORS } from 'features/dashboard/constants/orderSummaryColors';
import { useStyles } from 'features/dashboard/components/RechartsBarChart/styles';
import i18n from 'locales/i18n';

interface RechartsBarChartProps {
  data: RadialChartItem[];
  size?: SizeMeProps['size'];
}

type LabelRendererProps = Omit<SVGProps<SVGTextElement>, 'viewBox'> & LabelProps;

const LEGEND_RECT_WIDTH = 40;

const createCustomizedLabelRenderer = (textClassName: string, direction: string) => (props: LabelRendererProps) => {
  const { x, y, height, value, fill } = props;

  let legendX = x;
  const legendY = (y as number) + (height as number) * 2.5;

  let labelTextAdjustX = 2;
  if (direction === 'rtl' && legendX) {
    legendX = (legendX as number) - 40;
    labelTextAdjustX = -0.6;
  }
  return (
    <g>
      <rect x={legendX} y={legendY} width={LEGEND_RECT_WIDTH} height={height} fill={fill} />
      <text
        x={(legendX as number) + LEGEND_RECT_WIDTH * labelTextAdjustX}
        y={legendY + (height as number) * 0.85}
        className={textClassName}
      >
        {value}
      </text>
    </g>
  );
};

const createCustomizedPriceLabelRenderer =
  (textClassName: string, rootWidth: number, direction: string) => (props: LabelRendererProps) => {
    const { x, y, height, value, fill } = props;

    let legendX = x;
    const legendY = (y as number) + (height as number) * 2.5;

    let labelTextAdjustX = 0.8;

    if (direction === 'rtl') {
      legendX = (legendX as number) - 40;
      labelTextAdjustX = 0.2;
    }

    return (
      <g>
        <rect x={legendX} y={legendY} width={LEGEND_RECT_WIDTH} height={height} fill={fill} />

        <text x={rootWidth * labelTextAdjustX} y={legendY + (height as number) * 0.85} className={textClassName}>
          {Number((value as number)?.toFixed(2)).toLocaleString('currency', { maximumFractionDigits: 2 })}
        </text>
      </g>
    );
  };

const background = { fill: '#F2F2F2' };

export const RechartsBarChart = withSize()<RechartsBarChartProps>(({ data, size }) => {
  const classes = useStyles();

  const direction = i18n.dir();

  const renderCustomizedLabel = React.useMemo(
    () => createCustomizedLabelRenderer(classes.labelText, direction),
    [classes, direction]
  );
  const renderCustomizedPriceLabel = React.useMemo(
    () => createCustomizedPriceLabelRenderer(classes.totalText, size?.width ?? 0, direction),
    [classes, size, direction]
  );

  return (
    <ResponsiveContainer width="100%" height="100%" className={classes.root}>
      <BarChart layout="vertical" barCategoryGap="43%" width={150} height={40} data={data} className={classes.root}>
        {direction === 'rtl' ? <XAxis type="number" hide reversed /> : <XAxis type="number" hide />}
        <YAxis type="category" dataKey="name" hide />
        <Bar
          dataKey="total"
          fill="#8884d8"
          layout="horizontal"
          height={10}
          background={background}
          isAnimationActive={false}
          className={classes.root}
        >
          <LabelList dataKey="name" position="left" content={renderCustomizedLabel} />
          <LabelList dataKey="total" position="left" content={renderCustomizedPriceLabel} />

          {data.map((entry, index) => (
            <Cell key={entry.name} fill={ORDER_SUMMARY_COLORS[index % ORDER_SUMMARY_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
});
